import React, {
	useCallback, useEffect, useRef, useState,
} from 'react';
import lottie from 'lottie-web';
import useStyles from 'isomorphic-style-loader/useStyles';

import { Button } from '@sololearnorg/sol';

import {
	Container, DataService, ILearningExperience, i18n,
} from '../../../../symphony';
import { SlDataService } from '../../services/sl-data.service';
import { SlActions } from '../../services/ActionsService/sl-actions';
import { SlLEContext } from '../../global-constants';
import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';
import { useFadeOut } from '../../../../shared/public/SlHooks/useFadeOut';
import { SlTransition } from '../../../../shared/public/SlTransition/SlTransition';
import { formatDate, getLEStatus } from '../../utils/helpers';
import {
	ActionType,
	RequestType,
	OnboardingState,
} from '../../global-interface';

import loaderData from '../../animations/to_first_lesson_loader.json';
import loopData from '../../animations/to_first_lesson_loop_state.json';

import s from './Onboarding.scss';

export function Onboarding(): JSX.Element {
	useStyles(s);
	const [loaderCompleted, setLoaderCompleted] = useState<boolean>(false);

	const containerRef = useRef(null);
	const animationRef = useRef(null);

	const { onboarding$: state } = useContainerData(
		SlLEContext,
		SlDataService,
		['onboarding$'],
	);

	const { learningExperience$: learningExperience } = useContainerData(
		'global',
		DataService,
		['learningExperience$'],
	);

	const { isFadeOutStarted, startFadeOut } = useFadeOut(400);

	const { actions$ } = Container.take(SlLEContext, SlActions);

	const onLoopComplete = useCallback(() => {
		actions$.next({
			type: ActionType.OnOnboardingLoopIteration,
			requestType: RequestType.Parallel,
		});

		setLoaderCompleted(true);

		animationRef.current.destroy();
		animationRef.current = lottie.loadAnimation({
			container: containerRef.current,
			animationData: loopData,
			loop: false,
		});
		animationRef.current.play();
		animationRef.current.addEventListener('complete', onLoopComplete);
	}, [state]);

	useEffect(() => {
		animationRef.current = lottie.loadAnimation({
			container: containerRef.current,
			animationData: loaderData,
			loop: false,
		});
		animationRef.current.play();
		animationRef.current.addEventListener('complete', onLoopComplete);
	}, []);

	const onClick = (): void => {
		actions$.next({
			type: ActionType.OnOnboardingCtaClick,
			requestType: RequestType.Parallel,
		});
		startFadeOut(() => { });
	};

	const renderDescription = (le: ILearningExperience): JSX.Element => {
		switch (getLEStatus(le)) {
		case 'default':
			return (
				<>
					{i18n.t('web-le.onboarding.description')}
				</>
			);
		case 'early-bird':
			return (
				<>
					{i18n.t('web-le.onboarding.early-bird.heading-1')}
					<br />
					{i18n.t('web-le.onboarding.early-bird.heading-2')}
					{' '}
					<span style={{ color: '#AB88F3' }}>{formatDate(le.startDate)}</span>
					<br />
					<br />
					{i18n.t('web-le.onboarding.early-bird.heading-3')}
				</>
			);
		case 'launched':
			return (
				<>
					{i18n.t('web-le.onboarding.launch.heading-1')}
					<br />
					{i18n.t('web-le.onboarding.launch.heading-2')}
				</>
			);
		default:
			return null;
		}
	};

	const getButtonText = (le:ILearningExperience): string => {
		switch (getLEStatus(le)) {
		case 'default': return i18n.t('web-le.onboarding.button');
		case 'early-bird': return i18n.t('web-le.onboarding.early-bird.button');
		case 'launched': return i18n.t('web-le.onboarding.launch.button');
		default: return null;
		}
	};

	return learningExperience ? (
		<div className="le-onboarding">
			<div
				className="le-onboarding__animation-container"
				ref={containerRef}
			/>

			<div className="le-onboarding__description">
				{renderDescription(learningExperience)}
			</div>

			{(state === OnboardingState.Ready
				|| state === OnboardingState.Done)
				&& loaderCompleted && (
				<SlTransition
					fadeIn={{ delay: 0, duration: 0.3 }}
					fadeOut={{ delay: 0.1, duration: 0.3 }}
					isFadeOutStarted={isFadeOutStarted}
				>
					<Button
						className="le-onboarding__cta"
						onClick={onClick}
					>
						{getButtonText(learningExperience)}
					</Button>
				</SlTransition>
			)}
		</div>
	) : null;
}
